import { FC, memo, useState } from "react";

import { PencilFill } from "@hl/base-components/lib/assets/icons.generated";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { formatDateToUserLocale } from "@hl/communities-app/lib/lib/date-utils";
import {
  ActionIcon,
  Box,
  Group,
  Stack,
  Text,
  Tooltip,
  Anchor,
} from "@mantine/core";
import { Link, useNavigate } from "react-router-dom";

import { ReactComponent as CopyIcon } from "assets/icons/copy-fill.svg";
import { ReactComponent as ViewIcon } from "assets/icons/eye-fill.svg";
import { ReactComponent as DeleteIcon } from "assets/icons/trash-fill.svg";
import { TOOLS_URL, GATES_URL } from "config";
import DeleteModal from "~/features/pages/DeleteModal";

import * as styles from "./styles";

type PageIdentifier = {
  id: string;
  name: string | null;
  slug: string;
  created: string;
  gateId?: string;
  gateName?: string;
};

const HighlightPage: FC<PageIdentifier> = ({
  id,
  name,
  slug,
  created,
  gateId,
  gateName,
}) => {
  const [copied, setCopied] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const handleDeleteOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setOpenDeleteModal(true);
  };

  const viewPageUrl = GATES_URL.page.view.replace(":pageSlug", slug);
  const editPageUrl = TOOLS_URL.pages.edit.replace(":pageId", id);
  const navigate = useNavigate();

  const handleEditPage = async () => {
    navigate(editPageUrl);
  };

  const handleViewPage = async () => {
    window.open(viewPageUrl, "_blank");
  };

  const handleCopyOnClick = () => {
    navigator.clipboard.writeText(viewPageUrl);
    setCopied(true);
  };

  return (
    <Box key={id} role="listitem" py={20}>
      <Group position="apart">
        <Stack spacing={4}>
          <Text weight={500} size="lg">
            <Anchor
              href={viewPageUrl}
              target="_blank"
              rel="noreferrer"
              underline={false}
            >
              {name ?? slug}
            </Anchor>
          </Text>
          <Text size="sm">
            Created {formatDateToUserLocale(new Date(created).getTime())}
          </Text>
          {gateId ? (
            <Text color={TEXT_COLOR.SECONDARY} size="sm">
              {`Gated by ${gateName ? gateName : gateId}`}
            </Text>
          ) : (
            <Text color={TEXT_COLOR.SECONDARY} size="sm">
              Anyone can view this page
            </Text>
          )}
        </Stack>
        <Group spacing={8}>
          <Tooltip label="Edit page" withArrow>
            <ActionIcon
              size={40}
              onClick={handleEditPage}
              aria-label="edit page"
            >
              <PencilFill css={styles.iconStyle} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={copied ? "Copied!" : "Copy page link"} withArrow>
            <Text component={Link} to={"#"}>
              <ActionIcon
                size={40}
                onClick={handleCopyOnClick}
                aria-label="copy page link"
              >
                <CopyIcon css={styles.iconStyle} />
              </ActionIcon>
            </Text>
          </Tooltip>
          <Tooltip label="View page" withArrow>
            <Text component={Link} to={"#"}>
              <ActionIcon
                size={40}
                onClick={handleViewPage}
                aria-label="view page"
              >
                <ViewIcon css={styles.iconStyle} />
              </ActionIcon>
            </Text>
          </Tooltip>
          <Tooltip label="Delete page" withArrow>
            <Text component={Link} to={"#"}>
              <ActionIcon
                size={40}
                onClick={handleDeleteOnClick}
                aria-label="delete page"
              >
                <DeleteIcon css={styles.iconStyle} />
              </ActionIcon>
            </Text>
          </Tooltip>
        </Group>
      </Group>
      <DeleteModal
        onClose={() => setOpenDeleteModal(false)}
        pageId={id}
        opened={openDeleteModal}
      />
    </Box>
  );
};

export default memo(HighlightPage);
